import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Controller } from "react-hook-form";
import { useLocalization } from "@fluent/react";
import {
  Button,
  FormSection,
  RadioButtons,
  TextInput,
} from "@narmi/design_system";
import { ContentCard, catcat as cc, useNotificationContext } from "cerulean";
import Account from "byzantine/src/Account";
import { modules } from "byzantine";
import filters from "byzantine/src/filters";
import {
  useRecipient,
  useRecipients,
} from "byzantine/src/dbbl/businessLogic/entities/recipients";
import {
  useCurrencyContext,
  useFetchInternationalWireReasons,
} from "byzantine/src/dbbl/businessLogic/modules/internationalWires";
import AccountContext from "../../contexts/AccountContext";
import ComboAccountSelector from "../../ComboAccountSelector";
import CurrencyAmountInput from "../../../../cerulean/src/ReactHookForm/CurrencyAmountInput";
import DrawerLayout from "../../DrawerLayout";
import RecipientSelector from "../../transfer/RecipientSelector";
import CurrencySelector from "../../transfer/wires/InternationalWire/CurrencySelector";
import WireReasonSelector from "../../transfer/wires/InternationalWire/WireReasonSelector";
import { RecipientDrawer, DRAWER_TYPES } from "../../RecipientDrawer";
import InternationalWireRateDialog from "./InternationalWireRateDialog";
import RecipientCard from "../WireTemplateDrawer/RecipientCard";
import styles from "./InternationalWireTemplateForm.module.scss";

type InternationalWireTemplateFormProps = {
  onDismiss: () => void;
  templateId?: API.SwiftWireTemplateId;
};

const InternationalWireTemplateForm = ({
  onDismiss,
  templateId,
}: InternationalWireTemplateFormProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { accounts } = useContext(AccountContext);
  const { currencies: currenciesOrNull } = useCurrencyContext();
  const currencies = currenciesOrNull ?? [];
  const hasFx = currencies.length > 1;
  const { form, onRemoveRecipient, onSubmit, loading } =
    modules.internationalWires.useAddOrEditInternationalTemplateForm(
      templateId
    );

  const recipientId = form.watch("recipientId");
  const selectedCurrency = form.watch("recipientCurrency") || "";
  const watchedAmount = form.watch("amount");
  const watchedAmountCurrency = form.watch("amountCurrency");

  const selectedRecipient = useRecipient(recipientId);
  const recipients = useRecipients();
  const showAmountCurrencyCard =
    hasFx &&
    !!watchedAmount &&
    Number(watchedAmount) > 0 &&
    !!selectedCurrency &&
    selectedCurrency !== "USD";

  const isRecipientSwiftMissing =
    selectedRecipient && !selectedRecipient.swift_destination;

  const [showRecipientDrawer, setShowRecipientDrawer] = useState(false);
  const [showRatesDialog, setShowRatesDialog] = useState(false);
  const [reasonsRes, setReasonsRes] =
    useState<API.InternationalWireReasonResponse>({});
  const { fetchInternationalWireReasons } = useFetchInternationalWireReasons();

  useEffect(() => {
    if (currencies.length === 1 && !selectedCurrency) {
      form.setValue("recipientCurrency", currencies[0].currency);
    }
  }, [currencies, selectedCurrency, form]);

  useEffect(() => {
    // No recipientCurrency or no amount → clear amountCurrency
    if (!hasFx || !selectedCurrency || !watchedAmount) {
      if (watchedAmountCurrency) {
        form.setValue("amountCurrency", undefined);
      }
      return;
    }

    // Both exist → set to recipientCurrency unless user chose USD
    if (
      watchedAmountCurrency !== "USD" &&
      watchedAmountCurrency !== selectedCurrency
    ) {
      form.setValue("amountCurrency", selectedCurrency);
    }
  }, [hasFx, watchedAmount, selectedCurrency, watchedAmountCurrency, form]);

  const dest = selectedRecipient?.swift_destination;
  let reasonsParams: API.FetchInternationalWireReasons.Request | undefined;
  if (dest?.bic) {
    reasonsParams = { bic: dest.bic };
  } else if (
    dest?.correspondent_bank_bic &&
    dest.branch_address?.country_code
  ) {
    reasonsParams = { country: dest.branch_address.country_code };
  }

  useEffect(() => {
    if (reasonsParams) {
      fetchInternationalWireReasons(reasonsParams, {
        onSuccess: (response) => {
          setReasonsRes(response);
          const reasons = Object.values(response)?.[0];
          const currentReason = form.getValues("wireReason");
          if (
            !reasons?.length ||
            (currentReason && !reasons.some((r) => r.code === currentReason))
          ) {
            form.setValue("wireReason", undefined);
            form.setValue("wireReasonDescription", undefined);
          }
        },
      });
    } else {
      setReasonsRes({});
      form.setValue("wireReason", undefined);
      form.setValue("wireReasonDescription", undefined);
    }
  }, [selectedRecipient, fetchInternationalWireReasons, form]);

  const validAccounts = accounts.filter(
    (acc: Account) => acc.isInternal() && acc.isValidTransferSource()
  );

  const goBack = useCallback(() => onDismiss(), [onDismiss]);

  // setting error on recipientCurrency directly here
  // because schema does not have knowledge of existence of multiple currencies
  const onSave = useCallback(() => {
    if (
      hasFx &&
      watchedAmount &&
      Number(watchedAmount) > 0 &&
      !selectedCurrency
    ) {
      form.setError("recipientCurrency", {
        type: "manual",
        message: l10n.getString("form-generic-validation-required"),
      });
      return;
    }
    onSubmit(goBack);
  }, [hasFx, watchedAmount, selectedCurrency, form, l10n, goBack, onSubmit]);

  const handleOpenRecipientDrawer = () => {
    setShowRecipientDrawer(true);
  };

  const handleCloseRecipientDrawer = (newRecipientId?: API.RecipientId) => {
    const newRecipient = recipients.find((r) => r.id === newRecipientId);
    if (newRecipientId && newRecipient?.swift_destination) {
      form.setValue("recipientId", newRecipientId, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    } else if (isRecipientSwiftMissing) {
      form.setValue("recipientId", "" as API.RecipientId);
      sendNotification({
        type: "negative",
        text: l10n.getString("add-swift-details-error-banner"),
      });
    }
    setShowRecipientDrawer(false);
  };

  const onAddRecipientClick = () => {
    form.setValue("recipientId", "" as API.RecipientId, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    handleOpenRecipientDrawer();
  };

  const renderWireReasons = () => {
    const reasons = Object.values(reasonsRes)?.[0];
    if (!reasons?.length) return null;
    return (
      <div className={styles.field}>
        <Controller
          name="wireReason"
          control={form.control}
          render={({
            field: { onChange: controlOnChange, value },
            fieldState: { error },
          }) => {
            const onChange = (e: string) => {
              controlOnChange(e);
              form.setValue(
                "wireReasonDescription",
                filters.humanize(
                  reasons.find((reason) => reason.code === e)?.description || ""
                )
              );
            };
            return (
              <WireReasonSelector
                reasons={reasons}
                onChange={onChange}
                value={value ?? ""}
                label={l10n.getString("label-wire-template--wire-reason")}
                error={error?.message || ""}
              />
            );
          }}
        />
      </div>
    );
  };

  useEffect(() => {
    if (isRecipientSwiftMissing) {
      handleOpenRecipientDrawer();
    }
  }, [selectedRecipient]);

  return (
    <div className={styles.formContainer}>
      <DrawerLayout onSave={onSave} onCancel={goBack} loading={loading}>
        <div className={styles.container}>
          <h2 className={styles.header}>
            {templateId
              ? l10n.getString("heading-edit-template")
              : l10n.getString("heading-new-template")}
          </h2>

          <div className={styles.subHeader}>
            <FormSection title={l10n.getString("heading-recipient-details")} />
          </div>
          <div className={styles.recipients}>
            {recipientId && selectedRecipient ? (
              <RecipientCard
                recipientId={recipientId}
                onDelete={onRemoveRecipient}
              />
            ) : (
              <Controller
                name="recipientId"
                control={form.control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <RecipientSelector
                    onChange={onChange}
                    value={value}
                    label={l10n.getString("label-recipient")}
                    recipients={recipients}
                    onAddRecipientClick={onAddRecipientClick}
                    error={error?.message}
                    displayBottomAction
                  />
                )}
              />
            )}
          </div>

          <div className={styles.subHeader}>
            <FormSection title={l10n.getString("label-template-details")} />
          </div>

          <div className={styles.field}>
            <Controller
              name="name"
              control={form.control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <TextInput
                  label={l10n.getString("label-wire-template--template-name")}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={ref}
                  error={error?.message}
                />
              )}
            />
          </div>

          <div className={styles.field}>
            <Controller
              name="fromAccountId"
              control={form.control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <ComboAccountSelector
                  label={l10n.getString("label-from-account")}
                  value={value}
                  onChange={onChange}
                  isDestination={false}
                  accounts={validAccounts}
                  showAddExternalAccountLink={false}
                  errorText={error?.message}
                />
              )}
            />
          </div>

          {hasFx && (
            <div className={styles.field}>
              <Controller
                name="recipientCurrency"
                control={form.control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CurrencySelector
                    currencies={currencies}
                    onChange={(val: string) => {
                      onChange(val);
                      if (val) {
                        form.clearErrors("recipientCurrency");
                      }
                    }}
                    value={value ?? ""}
                    label={
                      watchedAmount && Number(watchedAmount) > 0
                        ? l10n.getString("label-currency-selector")
                        : l10n.getString("label-currency-selector-optional")
                    }
                    error={error?.message ?? ""}
                  />
                )}
              />
            </div>
          )}

          <div className={styles.field}>
            <CurrencyAmountInput
              name="amount"
              control={form.control}
              label={l10n.getString("label-wire-template--amount")}
              currency={watchedAmountCurrency || "USD"}
              showEndContent={!!watchedAmount && hasFx}
            />
            {hasFx && !showAmountCurrencyCard && (
              <div className={cc([styles.subText, "margin--top--xxs"])}>
                {l10n.getString("international-wire-template-amount-fx-hint")}
              </div>
            )}
            {showAmountCurrencyCard && (
              <div
                className={cc([styles.amountCurrencyCard, "margin--top--m"])}
              >
                <svg
                  className={styles.amountCurrencyCardAccent}
                  aria-hidden="true"
                >
                  <line x1="1" y1="0" x2="1" y2="100%" />
                </svg>
                <div className="margin--left--m">
                  <ContentCard kind="bordered" radiusSize="m">
                    <p className={styles.currencyCardSubheader}>
                      {l10n.getString(
                        "international-wire-form-currency-subheader"
                      )}
                    </p>
                    <Controller
                      name="amountCurrency"
                      control={form.control}
                      render={({ field: { onChange, value } }) => (
                        <RadioButtons
                          name="amountCurrency"
                          options={{
                            [l10n.getString(
                              "label-currency-radio-option-receives",
                              { currency: selectedCurrency }
                            )]: selectedCurrency,
                            [l10n.getString(
                              "label-currency-radio-option-send"
                            )]: "USD",
                          }}
                          value={value || selectedCurrency}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            onChange(e.target.value);
                          }}
                        />
                      )}
                    />
                    <Button
                      kind="plain"
                      onClick={() => setShowRatesDialog(true)}
                    >
                      {l10n.getString("international-wire-rates-learn-more")}
                    </Button>
                  </ContentCard>
                </div>
              </div>
            )}
          </div>

          {renderWireReasons()}

          <div className={styles.field}>
            <Controller
              name="memo"
              control={form.control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <TextInput
                  label={l10n.getString("label-wire-template--template-memo")}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value ?? ""}
                  ref={ref}
                  error={error?.message}
                />
              )}
            />
          </div>

          <div className={styles.field}>
            <Controller
              name="note"
              control={form.control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <TextInput
                  label={l10n.getString("label-wire-template--note")}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value ?? ""}
                  ref={ref}
                  error={error?.message}
                />
              )}
            />
          </div>

          <div className={styles.subText}>
            {l10n.getString("wire-template-optional-fields-hint")}
          </div>
        </div>
      </DrawerLayout>
      <RecipientDrawer
        handleClose={handleCloseRecipientDrawer}
        isOpen={showRecipientDrawer}
        fromInternationalWireFlow
        drawerType={recipientId ? DRAWER_TYPES.edit : DRAWER_TYPES.add}
        recipientId={recipientId}
      />
      <InternationalWireRateDialog
        isOpen={showRatesDialog}
        onDismiss={() => setShowRatesDialog(false)}
      />
    </div>
  );
};

export default InternationalWireTemplateForm;
